import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown, X } from 'lucide-react';
import './DateRangePicker.css';

interface DateRangePickerProps {
  startDate: Date;
  endDate: Date;
  onApply: (startDate: Date, endDate: Date) => void;
}

type PresetOption = 'last7days' | 'last30days' | 'thisMonth' | 'lastMonth' | 'custom';

const DateRangePicker: React.FC<DateRangePickerProps> = ({
  startDate,
  endDate,
  onApply,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);
  const [selectedPreset, setSelectedPreset] = useState<PresetOption>('last7days');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Format date for display
  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };

  // Format date for input
  const formatDateForInput = (date: Date): string => {
    return date.toISOString().split('T')[0];
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Update temp dates when props change
  useEffect(() => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, [startDate, endDate]);

  // Handle preset selection
  const handlePresetSelect = (preset: PresetOption) => {
    setSelectedPreset(preset);
    const today = new Date();
    let newStartDate = new Date();
    let newEndDate = new Date();

    switch (preset) {
      case 'last7days':
        newStartDate.setDate(today.getDate() - 7);
        newEndDate = today;
        break;
      case 'last30days':
        newStartDate.setDate(today.getDate() - 30);
        newEndDate = today;
        break;
      case 'thisMonth':
        newStartDate = new Date(today.getFullYear(), today.getMonth(), 1);
        newEndDate = today;
        break;
      case 'lastMonth':
        newStartDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        newEndDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'custom':
        // Keep current dates for custom
        return;
    }

    setTempStartDate(newStartDate);
    setTempEndDate(newEndDate);
  };

  // Handle apply
  const handleApply = () => {
    onApply(tempStartDate, tempEndDate);
    setIsOpen(false);
  };

  // Handle cancel
  const handleCancel = () => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setIsOpen(false);
  };

  return (
    <div className="date-range-picker" ref={dropdownRef}>
      <div className="date-range-picker-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span className="date-range-picker-label">Showing Date</span>
        <span className="date-range-picker-value">
          {formatDate(startDate)} - {formatDate(endDate)}
          <Calendar size={16} />
          <ChevronDown size={16} className={`chevron ${isOpen ? 'open' : ''}`} />
        </span>
      </div>

      {isOpen && (
        <div className="date-range-picker-dropdown">
          <div className="date-range-picker-header">
            <span>Select Date Range</span>
            <button className="date-range-picker-close" onClick={handleCancel}>
              <X size={18} />
            </button>
          </div>

          <div className="date-range-picker-presets">
            <button
              className={`preset-btn ${selectedPreset === 'last7days' ? 'active' : ''}`}
              onClick={() => handlePresetSelect('last7days')}
            >
              Last 7 Days
            </button>
            <button
              className={`preset-btn ${selectedPreset === 'last30days' ? 'active' : ''}`}
              onClick={() => handlePresetSelect('last30days')}
            >
              Last 30 Days
            </button>
            <button
              className={`preset-btn ${selectedPreset === 'thisMonth' ? 'active' : ''}`}
              onClick={() => handlePresetSelect('thisMonth')}
            >
              This Month
            </button>
            <button
              className={`preset-btn ${selectedPreset === 'lastMonth' ? 'active' : ''}`}
              onClick={() => handlePresetSelect('lastMonth')}
            >
              Last Month
            </button>
            <button
              className={`preset-btn ${selectedPreset === 'custom' ? 'active' : ''}`}
              onClick={() => handlePresetSelect('custom')}
            >
              Custom
            </button>
          </div>

          <div className="date-range-picker-inputs">
            <div className="date-input-group">
              <label>Start Date</label>
              <input
                type="date"
                value={formatDateForInput(tempStartDate)}
                onChange={(e) => {
                  setTempStartDate(new Date(e.target.value));
                  setSelectedPreset('custom');
                }}
                max={formatDateForInput(tempEndDate)}
              />
            </div>
            <div className="date-input-group">
              <label>End Date</label>
              <input
                type="date"
                value={formatDateForInput(tempEndDate)}
                onChange={(e) => {
                  setTempEndDate(new Date(e.target.value));
                  setSelectedPreset('custom');
                }}
                min={formatDateForInput(tempStartDate)}
                max={formatDateForInput(new Date())}
              />
            </div>
          </div>

          <div className="date-range-picker-actions">
            <button className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
            <button className="apply-btn" onClick={handleApply}>
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
